import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Col, Form } from 'react-bootstrap'
import { Button } from '../Button/Button'
import { ReactComponent as Sparkling } from '../../assets/images/sparkling.svg'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { $generateHtmlFromNodes } from '@lexical/html'
import { EditorState, LexicalEditor } from 'lexical'
import { WritableLexicalEditor } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import { LEXICAL_NODES } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/nodes'
import { RichTextToolbarPlugin } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { getEditorStateFromHtml, getEditorStateFromText, isEditorRichText } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/utils'
import classNames from 'classnames'
import './TemplateBox.scss'
import { TEMPLATE_TYPE_MAPPING } from './TemplateTypeMapping'

export interface TemplateBoxProps {
  onGenerate?: () => void
  shouldShowGenerate: boolean
  template: string
  defaultTemplate: string
  parentTemplate: string
  generatedTemplate: string | null
  customized: boolean
  showCustomizationSetting: boolean
  onChangeTemplate: (newTemplate: string) => void
  onChangeCustomized?: () => void
  shouldReset: boolean
  disabled?: boolean
  typeId: string
}

export const TemplateBox: React.FC<TemplateBoxProps> = ({
  template,
  defaultTemplate,
  parentTemplate,
  generatedTemplate,
  onGenerate,
  shouldShowGenerate,
  showCustomizationSetting,
  customized,
  onChangeTemplate,
  onChangeCustomized,
  shouldReset,
  disabled,
  typeId,
}) => {
  const editorRef = useRef<LexicalEditor>(null)

  const [initialEditorState, setInitialEditorState] = useState<EditorState>(() => {
    return getEditorStateFromHtml(template)
  })

  const [defaultTemplateEditorState] = useState<EditorState>(() => {
    return getEditorStateFromHtml(defaultTemplate)
  })

  const [parentTemplateEditorState] = useState<EditorState>(() => {
    return getEditorStateFromHtml(parentTemplate)
  })

  useEffect(() => {
    if (generatedTemplate) {
      return
    }
    setInitialEditorState(getEditorStateFromHtml(template))
  }, [template, generatedTemplate])

  const disabledByCustomization = showCustomizationSetting && !customized

  const resetToDefaultTemplate = useCallback(() => {
    const editor = editorRef.current
    if (!editor || !defaultTemplateEditorState) {
      return
    }
    editor.setEditorState(defaultTemplateEditorState)
  }, [defaultTemplateEditorState])

  const resetToParentTemplate = useCallback(() => {
    const editor = editorRef.current
    if (!editor || !parentTemplateEditorState) {
      return
    }
    editor.setEditorState(parentTemplateEditorState)
  }, [parentTemplateEditorState])

  const resetToInitialTemplate = useCallback(() => {
    const editor = editorRef.current
    if (!editor) {
      return
    }
    editor.setEditorState(initialEditorState)
  }, [initialEditorState])

  useEffect(() => {
    if (shouldReset) {
      resetToInitialTemplate()
    }
  }, [shouldReset, resetToInitialTemplate])

  const setTemplate = useCallback((newTemplate: string) => {
    const editor = editorRef.current
    if (!editor) {
      return
    }
    editor.setEditorState(getEditorStateFromHtml(newTemplate))
  }, [])

  useEffect(() => {
    if (disabledByCustomization) {
      resetToParentTemplate()
    } else {
      resetToInitialTemplate()
    }
  }, [disabledByCustomization, resetToInitialTemplate, resetToParentTemplate])

  useEffect(() => {
    if (generatedTemplate) {
      setTemplate(generatedTemplate)
      onChangeTemplate(generatedTemplate)
    }
  }, [generatedTemplate, onChangeTemplate, setTemplate])

  useEffect(() => {
    editorRef.current?.setEditable(!disabled && !disabledByCustomization)
  }, [disabled, disabledByCustomization])

  const handleReset = () => {
    resetToDefaultTemplate()
  }

  const handleChange = () => {
    const editor = editorRef.current
    if (!editor) {
      return
    }
    const editorState = editor.getEditorState()
    editorState.read(() => {
      onChangeTemplate($generateHtmlFromNodes(editor, null))
    })
  }

  return (
    <>
      <div className='d-flex justify-content-between'>
        <OverlayTrigger
          placement='right'
          overlay={
            <Tooltip className='template-tooltip'>
              <Col>
                Please note that the text enclosed in [] will be altered based on the content of the input inside. Standalone plain text will remain unchanged.
              </Col>
            </Tooltip>
          }
        >
          {({ ref, ...triggerHandler }) => (
            <div className='d-flex align-items-center gap-2 mb-1'>
              <div className='paragraph-medium'>{TEMPLATE_TYPE_MAPPING[typeId] ?? 'Template'}</div>
              <i {...triggerHandler} ref={ref} className='bi bi-info-circle' />
            </div>
          )}
        </OverlayTrigger>
        {showCustomizationSetting ? (
          <Form.Check className='form-check' checked={customized} onChange={onChangeCustomized} label='Customize' disabled={disabled} />
        ) : null}
      </div>
      <Col>
        <div className='template-box'>
          <div className='template-dynamic-panel'>
            {initialEditorState ? (
              <WritableLexicalEditor
                className={classNames('p-0 template-box-editor', { 'zocks-text-gray-500': disabled || disabledByCustomization })}
                editorContainerClassName={'flex-grow-1 px-4 py-3'}
                onChange={handleChange}
                initialEditorState={initialEditorState}
                lexicalNodes={LEXICAL_NODES}
                header={!disabled && !disabledByCustomization ? <RichTextToolbarPlugin className='px-3 zocks-py-0.75 gap-2' /> : null}
                ref={editorRef}
              />
            ) : null}
            <div className='d-flex justify-content-between p-3'>
              <Button className='item button-small text-decoration-none' onClick={handleReset} variant='link' disabled={disabled || disabledByCustomization}>
                <i className='bi bi-arrow-clockwise fs-6' />
                <div className='text-decoration-underline'>Reset default</div>
              </Button>
              {!shouldShowGenerate && (
                <Button className='item button-small' onClick={onGenerate} variant='dark' disabled={disabled || disabledByCustomization}>
                  <Sparkling className='zocks-text-white' />
                  Generate template
                </Button>
              )}
            </div>
          </div>
        </div>
      </Col>
    </>
  )
}
